import { useEffect, useState } from "react";
import { BadgeCheck, Copy, KeyRound, Link2, Mail, ShieldCheck, Smartphone, Unlink } from "lucide-react";
import { api, type Account, type AuthChannel } from "./api";

type BindState = { channel: AuthChannel; target: string; code: string; sent: boolean; countdown: number } | null;

export function AccountSecurity({ onUpdated }: { onUpdated?: (account: Account) => void }) {
  const [profile, setProfile] = useState<Account>();
  const [bind, setBind] = useState<BindState>(null);
  const [password, setPassword] = useState({ current: "", next: "", confirm: "" });
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  async function reload() {
    const account = await api.account();
    setProfile(account);
    onUpdated?.(account);
  }

  useEffect(() => { void reload().catch((cause) => setError(cause instanceof Error ? cause.message : "账户信息加载失败")); }, []);
  useEffect(() => {
    if (!bind || bind.countdown <= 0) return;
    const timer = window.setInterval(() => setBind((value) => value ? { ...value, countdown: Math.max(0, value.countdown - 1) } : null), 1_000);
    return () => window.clearInterval(timer);
  }, [bind]);

  function notify(text: string) { setMessage(text); setError(""); }

  async function copyMemberNo() {
    if (!profile?.memberNo) return;
    try { await navigator.clipboard.writeText(profile.memberNo); setCopied(true); window.setTimeout(() => setCopied(false), 2_000); }
    catch { setError("复制失败，请手动记录会员账号"); }
  }

  async function sendBindCode() {
    if (!bind) return;
    const valid = bind.channel === "sms" ? /^1\d{10}$/.test(bind.target) : /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(bind.target);
    if (!valid) return setError(bind.channel === "sms" ? "请输入正确的11位手机号" : "请输入正确的邮箱地址");
    setBusy(true); setError("");
    try {
      const result = await api.requestBindCode(bind.channel, bind.target);
      setBind({ ...bind, sent: true, countdown: result.retryAfterSeconds });
      notify(result.debugCode ? `开发环境验证码：${result.debugCode}` : "验证码已发送，5分钟内有效");
    } catch (cause) { setError(cause instanceof Error ? cause.message : "验证码发送失败"); }
    finally { setBusy(false); }
  }

  async function confirmBind() {
    if (!bind || bind.code.length !== 6) return;
    setBusy(true); setError("");
    try { await api.bindChannel(bind.channel, bind.target, bind.code); setBind(null); await reload(); notify("登录渠道绑定成功"); }
    catch (cause) { setError(cause instanceof Error ? cause.message : "绑定失败"); }
    finally { setBusy(false); }
  }

  async function unbind(channel: AuthChannel) {
    if (!window.confirm(`确认解绑${channel === "sms" ? "手机号" : "邮箱"}？`)) return;
    setBusy(true); setError("");
    try { await api.unbindChannel(channel); await reload(); notify("登录渠道已解绑"); }
    catch (cause) { setError(cause instanceof Error ? cause.message : "解绑失败"); }
    finally { setBusy(false); }
  }

  async function savePassword() {
    setError("");
    if (password.next.length < 8) return setError("新密码长度需为8-64位");
    if (password.next !== password.confirm) return setError("两次输入的新密码不一致");
    if (profile?.hasPassword && !password.current) return setError("请输入当前密码");
    setBusy(true);
    try {
      await api.setPassword(password.next, profile?.hasPassword ? password.current : undefined);
      setPassword({ current: "", next: "", confirm: "" });
      await reload();
      notify(profile?.hasPassword ? "密码已更新" : "密码设置成功");
    } catch (cause) { setError(cause instanceof Error ? cause.message : "密码保存失败"); }
    finally { setBusy(false); }
  }

  if (!profile) return <section className="panel account-security-panel">{error || "正在加载账户与安全信息…"}</section>;

  return <div className="security-sections">
    {(message || error) && <p className={`form-message ${error ? "error" : ""}`} role="status">{error || message}</p>}
    <section className="panel account-security-panel">
      <div className="panel-head"><div><h2>账号信息</h2><p>三个学霸阁站点通用的会员身份与登录渠道。</p></div><BadgeCheck size={24} /></div>
      <div className="identity-grid">
        <span><small>会员账号</small><strong>{profile.memberNo || "生成中"}</strong><button type="button" onClick={copyMemberNo}><Copy size={14} />{copied ? "已复制" : "复制"}</button></span>
        <span><small>手机号</small><strong>{profile.mobileMasked || "未绑定"}</strong><em>{profile.mobileVerified ? "已验证" : "未验证"}</em></span>
        <span><small>邮箱</small><strong>{profile.emailMasked || "未绑定"}</strong><em>{profile.emailVerified ? "已验证" : "未验证"}</em></span>
      </div>
    </section>
    <section className="panel account-security-panel">
      <div className="panel-head"><div><h2>登录渠道</h2><p>可补充手机号或邮箱；至少保留一种已验证渠道。</p></div><ShieldCheck size={24} /></div>
      <div className="channel-list">
        <div><span><Smartphone size={17} />手机号</span><strong>{profile.mobileMasked || "未绑定"}</strong>{profile.mobileVerified ? <button className="button small" disabled={busy} onClick={() => unbind("sms")}><Unlink size={14} />解绑</button> : <button className="button small" disabled={busy} onClick={() => setBind({ channel: "sms", target: "", code: "", sent: false, countdown: 0 })}><Link2 size={14} />绑定</button>}</div>
        <div><span><Mail size={17} />邮箱</span><strong>{profile.emailMasked || "未绑定"}</strong>{profile.emailVerified ? <button className="button small" disabled={busy} onClick={() => unbind("email")}><Unlink size={14} />解绑</button> : <button className="button small" disabled={busy} onClick={() => setBind({ channel: "email", target: "", code: "", sent: false, countdown: 0 })}><Link2 size={14} />绑定</button>}</div>
      </div>
      {bind && <div className="security-form"><h3>{bind.channel === "sms" ? "绑定手机号" : "绑定邮箱"}</h3><label>{bind.channel === "sms" ? "手机号" : "邮箱"}<input autoComplete={bind.channel === "sms" ? "tel" : "email"} value={bind.target} placeholder={bind.channel === "sms" ? "请输入11位手机号" : "请输入邮箱地址"} onChange={(event) => setBind({ ...bind, target: bind.channel === "sms" ? event.target.value.replace(/\D/g, "") : event.target.value.trim() })} /></label>{bind.sent && <label>验证码<input autoComplete="one-time-code" inputMode="numeric" maxLength={6} value={bind.code} placeholder="6位验证码" onChange={(event) => setBind({ ...bind, code: event.target.value.replace(/\D/g, "") })} /></label>}<div>{!bind.sent || bind.countdown === 0 ? <button className="button primary" disabled={busy} onClick={sendBindCode}>发送验证码</button> : <button className="button primary" disabled={busy || bind.code.length !== 6} onClick={confirmBind}>确认绑定</button>}<button className="text-button" type="button" onClick={() => setBind(null)}>取消</button>{bind.sent && bind.countdown > 0 && <small>{bind.countdown} 秒后可重发</small>}</div></div>}
    </section>
    <section className="panel account-security-panel">
      <div className="panel-head"><div><h2>{profile.hasPassword ? "修改密码" : "设置密码"}</h2><p>设置后可用会员账号、手机号或邮箱登录。</p></div><KeyRound size={24} /></div>
      <form className="security-form password-grid" onSubmit={(event) => { event.preventDefault(); void savePassword(); }}><input className="security-username" autoComplete="username" value={profile.memberNo} readOnly aria-hidden="true" tabIndex={-1} />{profile.hasPassword && <label>当前密码<input autoComplete="current-password" type="password" maxLength={64} value={password.current} onChange={(event) => setPassword({ ...password, current: event.target.value })} /></label>}<label>新密码<input autoComplete="new-password" type="password" maxLength={64} placeholder="8-64位" value={password.next} onChange={(event) => setPassword({ ...password, next: event.target.value })} /></label><label>确认新密码<input autoComplete="new-password" type="password" maxLength={64} value={password.confirm} onChange={(event) => setPassword({ ...password, confirm: event.target.value })} /></label><button className="button primary" type="submit" disabled={busy}>{profile.hasPassword ? "更新密码" : "设置密码"}</button></form>
    </section>
  </div>;
}
